import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Trophy, Users, Calendar, Lightning, Scales, Medal, ShieldStar, Clock, Target,
  CheckCircle, Warning, Scroll, Crown, HandHeart, CurrencyDollar, UsersThree,
  TreeStructure, FilePdf, Share, Fire, Sparkle, CaretRight,
} from "@phosphor-icons/react";

const SECTIONS = [
  {
    id: "getting-started",
    title: "Getting Started",
    icon: Sparkle,
    subsections: [
      {
        title: "Signing in",
        body: (
          <>
            <p>Click <strong>Sign In</strong> at the top-right. Authenticate with Google (via Emergent-managed OAuth).</p>
            <p>After first sign-in, you'll land on the Dashboard. Personalised content (stats, alerts, finances) unlocks once you <em>claim</em> your player profile.</p>
          </>
        ),
      },
      {
        title: "Claiming your profile",
        body: (
          <>
            <ol className="list-decimal list-inside space-y-1">
              <li>Go to <Link to="/players" className="text-primary underline">Players</Link>.</li>
              <li>Find your name → click <strong>Claim Profile</strong>.</li>
              <li>Admin reviews the request — once approved, your email is linked to that player card.</li>
              <li>Your Dashboard, Smart Alerts, Finances and Upcoming Matches become available.</li>
            </ol>
          </>
        ),
      },
      {
        title: "Uploading your photo",
        body: (
          <>
            <p>On your <Link to="/players" className="text-primary underline">profile</Link> (after claim), click the avatar to upload. Photo appears across rankings, match pages, and share cards.</p>
          </>
        ),
      },
    ],
  },
  {
    id: "dashboard",
    title: "Dashboard (Home)",
    icon: Trophy,
    subsections: [
      {
        title: "Public view",
        body: <p>Logo, hero, active tournaments, Top-5 rankings, match calendar, leaderboards (centuries, high breaks, most wins, titles), and a sign-in CTA.</p>,
      },
      {
        title: "Signed-in view",
        body: (
          <ul className="list-disc list-inside space-y-1">
            <li><strong>Welcome bar</strong> — compact header with your first name.</li>
            <li><strong>Your Player Card</strong> — tier, rank, recent form, achievements.</li>
            <li><strong>Upcoming Matches</strong> — date, time, <em>table</em> (T1/T2), opponent.</li>
            <li><strong>Smart Alerts</strong> — rotating insights from 5 engines (see below).</li>
            <li><strong>My Finance</strong> — fees due, payouts received.</li>
            <li><strong>Match Calendar</strong> — "All" and "My Matches" tabs.</li>
          </ul>
        ),
      },
      {
        title: "Smart Alerts (5 rotating engines)",
        body: (
          <>
            <p>Click the refresh icon on the card to cycle the insight:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li><strong>Qualification & Progression</strong> — group-stage status.</li>
              <li><strong>Ranking Movement</strong> — rank climbs, Top-10 milestones.</li>
              <li><strong>Competitive Context</strong> — upsets, close contests.</li>
              <li><strong>Performance Momentum</strong> — win streaks, form.</li>
              <li><strong>Next Opponent Intelligence</strong> — scouting report for next fixture.</li>
            </ol>
            <p className="text-xs text-muted-foreground mt-1">Each engine has 3–4 rotating templates; the same message won't repeat within your last 5 alerts.</p>
          </>
        ),
      },
    ],
  },
  {
    id: "rankings",
    title: "Rankings",
    icon: Crown,
    subsections: [
      {
        title: "Tiers",
        body: (
          <>
            <Badge className="mr-1 border-secondary/40 text-secondary" variant="outline">Premier</Badge>
            <Badge variant="outline">Challenger</Badge>
            <p className="mt-1">Admin can promote/demote via Admin → Players tab.</p>
          </>
        ),
      },
      {
        title: "How points are calculated",
        body: (
          <>
            <p>Match points are awarded on <strong>completed</strong> matches based on:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Tournament weight (Major / Minor / League)</li>
              <li>Round (group → final gets higher multiplier)</li>
              <li>Win / walkover status</li>
              <li>Handicap adjustment (if active for the tournament)</li>
            </ul>
            <p className="text-xs text-muted-foreground mt-1">Friendly matches do <em>not</em> award ranking points (they still update ELO & H2H).</p>
          </>
        ),
      },
      {
        title: "ELO rating",
        body: (
          <>
            <p>Every player starts at <strong>1500</strong>. Each completed match applies a ±delta based on win/loss + rating difference.</p>
            <p>Admin can rebaseline Premier ELOs to 1500 + current tournament delta (Admin → Settings → Rebaseline Premier ELO).</p>
          </>
        ),
      },
    ],
  },
  {
    id: "tournaments",
    title: "Tournaments",
    icon: Trophy,
    subsections: [
      {
        title: "Formats supported",
        body: (
          <ul className="list-disc list-inside space-y-1">
            <li><strong>Knockout</strong> — single-elimination bracket.</li>
            <li><strong>Groups → Knockout</strong> — round-robin groups feed a knockout with byes & best-3rd-place rule.</li>
            <li><strong>League</strong> — round-robin all-play-all.</li>
          </ul>
        ),
      },
      {
        title: "Setup Wizard (Admin)",
        body: (
          <ol className="list-decimal list-inside space-y-1">
            <li>Admin → Tournaments → <strong>+ New Tournament</strong></li>
            <li>Enter name, date, type (Major / Minor / League), format</li>
            <li>Pick venues (defaults to <em>T1</em> / <em>T2</em>)</li>
            <li>Set <strong>Scoring Mode</strong>: self / referee-only</li>
            <li>Set <strong>Scoring Type</strong>: live / submission</li>
            <li>Register players (drag/drop or select)</li>
            <li>AI Group Generator (Gemini) can auto-build balanced groups</li>
            <li>Save → system auto-schedules matches using fixed slots (see Scheduling below)</li>
          </ol>
        ),
      },
      {
        title: "Manage Groups (Admin)",
        body: (
          <>
            <p>Tournament Detail → <strong>Manage Groups</strong>:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Check multiple players → move to another group in one action</li>
              <li>Add/remove individual players, add new groups</li>
              <li>Save → group-stage matches regenerate automatically</li>
            </ul>
          </>
        ),
      },
      {
        title: "Group → Knockout Progression",
        body: (
          <>
            <p>Tournament Detail → <strong>Generate Bracket</strong> (admin, groups_knockout format only):</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Picks top N per group (admin-configured)</li>
              <li>Optional best-3rd-place injection</li>
              <li>Assigns byes automatically for non-power-of-2 brackets</li>
              <li>Preview → finalise → knockout matches created</li>
            </ul>
          </>
        ),
      },
      {
        title: "Export PDF report",
        body: <p>Tournament Detail → <strong>Export PDF</strong>. Includes standings, bracket, match list, top breaks.</p>,
      },
    ],
  },
  {
    id: "scheduling",
    title: "Scheduling & Fixtures",
    icon: Calendar,
    subsections: [
      {
        title: "Auto-scheduling rule",
        body: (
          <>
            <p>Window: <strong>19:00 – 22:45</strong> (225 min).</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Best-of ≤ 3 → 112-min slot → <strong>2 matches per table per day</strong> (4 across 2 tables)</li>
              <li>Best-of ≥ 5 → 225-min slot → <strong>1 match per table per day</strong> (2 across 2 tables)</li>
              <li>Player-day cap: 1 match / player / day</li>
            </ul>
            <p>Admin can toggle fixed-slot on/off and tune slot minutes in Admin → Settings → Scheduling & Tables.</p>
          </>
        ),
      },
      {
        title: "Admin reschedule",
        body: (
          <>
            <p>On any scheduled match: <strong>Reschedule</strong> button (Admin → Results or Tournament Detail):</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Grid of every venue/table/time-slot for the chosen date</li>
              <li>Green = free → click to assign</li>
              <li>Occupied → click to <strong>atomic swap</strong> with that match</li>
              <li><strong>Postpone</strong> button → clears date/time/table; keeps players & format</li>
            </ul>
          </>
        ),
      },
      {
        title: "Player reschedule requests",
        body: (
          <>
            <p>Claimed players can open their profile → <strong>Request Reschedule</strong> on any upcoming match. They provide reason + optional preferred date/time.</p>
            <p>Admin sees pending requests in Admin → Results (pinned panel) → approve (opens Reschedule dialog) or reject with a note. Opponent gets notified both ways.</p>
          </>
        ),
      },
    ],
  },
  {
    id: "scoring",
    title: "Scoring",
    icon: Lightning,
    subsections: [
      {
        title: "Scoring Mode — who can score?",
        body: (
          <>
            <p><strong>self</strong> — admin, assigned referee, or either participating player (with claimed profile) can submit. Opponent confirmation required if a player submits.</p>
            <p><strong>referee_only</strong> — only admin or the assigned referee can submit.</p>
          </>
        ),
      },
      {
        title: "Scoring Type — how is it entered?",
        body: (
          <>
            <p><strong>Submission</strong> — enter final frame totals + optional highest break + notes after the match is played. Fast, trust-based for club nights.</p>
            <p><strong>Live</strong> — full shot-by-shot snooker engine (reds, colours, fouls, breaks). Best with a referee.</p>
            <p>Switch is guarded — if scoring data already exists, admin must <strong>Reset match</strong> first.</p>
          </>
        ),
      },
      {
        title: "Live match controls (Admin)",
        body: (
          <ul className="list-disc list-inside space-y-1">
            <li><strong>Pause</strong> — hides the match from Match Centre, preserves scores.</li>
            <li><strong>Resume</strong> — re-enters it live.</li>
            <li><strong>Switch to Live</strong> — flips submission → live mode.</li>
            <li><strong>Reset</strong> — wipes scores + ELO impact for that match only; schedule preserved.</li>
          </ul>
        ),
      },
      {
        title: "Confirming / disputing",
        body: (
          <p>If opponent submits first, you'll see <strong>Confirm Match Result</strong> on your profile. Confirm → finalised + stats apply. Dispute → admin reviews.</p>
        ),
      },
    ],
  },
  {
    id: "friendly",
    title: "Friendly Matches",
    icon: HandHeart,
    subsections: [
      {
        title: "What they are",
        body: <p>Exhibition matches between two members. <strong>Impact</strong>: ELO + head-to-head stats. <strong>Don't impact</strong>: ranking points, tournament structure.</p>,
      },
      {
        title: "Logging",
        body: <p>Claimed player → profile → <strong>Log Friendly</strong>. Enter opponent + frames. Opponent sees it under H2H immediately.</p>,
      },
    ],
  },
  {
    id: "handicaps",
    title: "Handicap System",
    icon: Scales,
    subsections: [
      {
        title: "How it works",
        body: <p>Each player has an integer handicap (0 by default). When a tournament uses handicap frames, the weaker player gets a start before ELO/points are computed.</p>,
      },
      {
        title: "Admin overrides",
        body: (
          <>
            <p>Admin → Handicap tab:</p>
            <ul className="list-disc list-inside space-y-1">
              <li><strong>Load Suggestions</strong> — generates system-suggested values from rank + win-rate.</li>
              <li>Type a value in the <em>Set</em> column → saves on blur; player is flagged 🔒 <strong>manual</strong>.</li>
              <li><strong>Apply (keep manual)</strong> — suggestion applies to everyone except manually-set players.</li>
              <li><strong>Apply & override manual</strong> — destructive, overrides locks (confirmation required).</li>
            </ul>
          </>
        ),
      },
    ],
  },
  {
    id: "finance",
    title: "Finance",
    icon: CurrencyDollar,
    subsections: [
      {
        title: "Tournament fees & prize pool",
        body: <p>Admin sets entry fee and prize-pool distribution per tournament. Players see their own dues under <strong>My Finance Summary</strong> on Dashboard.</p>,
      },
      {
        title: "Admin ledger",
        body: <p>Admin → Finance tab → full ledger of fees collected, payouts owed, and manual adjustments. Export coming soon.</p>,
      },
    ],
  },
  {
    id: "players-admin",
    title: "Player Management (Admin)",
    icon: Users,
    subsections: [
      {
        title: "CRUD",
        body: (
          <p>Admin → Players: create, edit (name, age, tier, handicap, mobile, ITS, photo), bulk delete. Bulk delete also clears fees, payouts, ELO history, and match points for deleted players.</p>
        ),
      },
      {
        title: "Claim approvals",
        body: <p>Admin → Claim Requests: approve/reject profile claims. Email linked to player record.</p>,
      },
      {
        title: "Past Achievements & Honours",
        body: (
          <p>Admin → any player profile → <strong>Past Achievements & Honours</strong> card → <strong>+ Add</strong>. Record titles, year, tournament, venue, notes. Purely display — no effect on ELO, ranking, or stats.</p>
        ),
      },
    ],
  },
  {
    id: "share",
    title: "Share Match Results",
    icon: Share,
    subsections: [
      {
        title: "Auto-generated social card",
        body: (
          <p>Every completed match has a <strong>Share</strong> button. Generates a 1200×630 PNG with player photos (or initials), score, winner badge, tournament, venue, date, and <em>"Powered by Blue Digital"</em> footer. Use via WhatsApp (Web Share API attaches the image), X, or download.</p>
        ),
      },
    ],
  },
  {
    id: "admin-overview",
    title: "Admin Tools Overview",
    icon: ShieldStar,
    subsections: [
      {
        title: "Tabs",
        body: (
          <ul className="list-disc list-inside space-y-1">
            <li><strong>Tournaments</strong> — setup, edit, delete, export PDF</li>
            <li><strong>Players</strong> — CRUD + bulk delete + tier changes</li>
            <li><strong>Results</strong> — player filter, reschedule, share, reschedule-request inbox</li>
            <li><strong>Referees</strong> — assign referee, scoring mode, scoring type, reset match</li>
            <li><strong>Handicap</strong> — suggestions, manual overrides, bulk apply</li>
            <li><strong>Finance</strong> — ledger, fees, payouts</li>
            <li><strong>Disputes</strong> — review contested results</li>
            <li><strong>Claim Requests</strong> — approve profile claims</li>
            <li><strong>Settings</strong> — seasons, scheduling, admin management, ELO rebaseline, point weights</li>
          </ul>
        ),
      },
      {
        title: "Admin management",
        body: <p>Admin → Settings → Admin Management: add/revoke admins by email or by selecting a claimed player profile.</p>,
      },
    ],
  },
];


function SubSection({ title, body }) {
  return (
    <div className="border-l-2 border-primary/30 pl-3 py-1" data-testid="help-subsection">
      <h4 className="font-heading font-semibold text-sm mb-1">{title}</h4>
      <div className="text-sm space-y-1 text-muted-foreground leading-relaxed">{body}</div>
    </div>
  );
}


export default function Help() {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6" data-testid="help-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Scroll className="w-8 h-8 text-primary" weight="fill" />
        <div>
          <h1 className="font-heading text-2xl font-bold" data-testid="help-title">System Documentation</h1>
          <p className="text-sm text-muted-foreground">
            Complete feature guide for Annadil Burhani DSM Snooker Ranking System
          </p>
        </div>
      </div>

      {/* Table of contents */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Contents</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
            {SECTIONS.map(s => {
              const Icon = s.icon;
              return (
                <a key={s.id} href={`#${s.id}`}
                  className="flex items-center gap-2 p-2 rounded-md hover:bg-muted/30 text-sm transition-colors"
                  data-testid={`toc-${s.id}`}
                >
                  <Icon className="w-4 h-4 text-primary shrink-0" weight="fill" />
                  <span className="flex-1 truncate">{s.title}</span>
                  <CaretRight className="w-3 h-3 text-muted-foreground" />
                </a>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Sections */}
      {SECTIONS.map(section => {
        const Icon = section.icon;
        return (
          <Card key={section.id} id={section.id} className="bg-card border-border scroll-mt-20" data-testid={`section-${section.id}`}>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 font-heading text-lg">
                <Icon className="w-5 h-5 text-primary" weight="fill" />
                {section.title}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {section.subsections.map((ss, i) => <SubSection key={i} {...ss} />)}
            </CardContent>
          </Card>
        );
      })}

      <div className="text-center text-xs text-muted-foreground py-4">
        Annadil Burhani DSM Snooker · Powered by Blue Digital
      </div>
    </motion.div>
  );
}
